import {
  CollectionData,
  CollectionSortRuleInDirection,
} from '@/api/generated/schemas';
import type {
  CollectionFilterGroupIn,
  CollectionSortRuleIn,
} from '@/api/generated/schemas';

export interface CollectionDataValues {
  collection_data?: CollectionData;
  publication_custom_filters?: CollectionFilterGroupIn | null;
  publication_custom_sorting?: CollectionSortRuleIn[] | null;
  publication_custom_limit?: number | null;
}

/**
 * Drop half-filled rows (a direction picked but no field yet) and default a
 * missing direction. An empty result becomes null so the API clears the value.
 */
export const normalizeCollectionSorting = (
  rules?: CollectionSortRuleIn[] | null
): CollectionSortRuleIn[] | null => {
  const cleaned = (rules ?? [])
    .filter((rule) => rule?.field)
    .map((rule) => ({
      field: rule.field,
      direction: rule.direction ?? CollectionSortRuleInDirection.asc,
    }));
  return cleaned.length ? cleaned : null;
};

/**
 * Clean up the dynamic-collection payload before submit:
 * - UNSET (fields never mounted) → omit filter/sort/limit from the payload.
 * - STATIC → drop custom filter/limit but KEEP sorting: a static collection
 *   still honours its stored order when listing publications.
 * - DYNAMIC → normalize sorting, null out an empty limit.
 */
export const normalizeCollectionData = <T extends CollectionDataValues>(
  values: T
): T => {
  // Tab never opened: collection_data field not registered — omit
  // publication_custom_* so PATCH (exclude_unset) preserves stored values.
  if (values.collection_data === undefined) {
    const rest = { ...values };
    delete rest.publication_custom_filters;
    delete rest.publication_custom_sorting;
    delete rest.publication_custom_limit;
    return rest;
  }

  if (values.collection_data !== CollectionData.dynamic) {
    return {
      ...values,
      publication_custom_filters: {},
      publication_custom_sorting: normalizeCollectionSorting(
        values.publication_custom_sorting
      ),
      publication_custom_limit: null,
    };
  }

  return {
    ...values,
    publication_custom_sorting: normalizeCollectionSorting(
      values.publication_custom_sorting
    ),
    publication_custom_limit: values.publication_custom_limit ?? null,
  };
};
